import React from "react";
import styles from "./OfficeAddress.module.scss";
import CustomImage from "../CustomImage";
import Chevron from "../UI/Icons/Chevron";
import { BRANCH_OFFICES, HEAD_OFFICE } from "../../utils/constants";
import LazyMedia from "../LazyMedia";
import useIsMobile from "../../hooks/useIsMobile";
import MapPin from "../UI/Icons/MapPin";
import Phone from "../UI/Icons/Phone";
import Mail from "../UI/Icons/Mail";
import Clock from "../UI/Icons/Clock";

export interface HeadOffice {
  badge: string;
  name: string;
  address: string;
  callLines: string[];
  email: string;
  hours: string;
  mapHref: string;
  image: string;
  mapImage: string;
}

export interface BranchOffice {
  id: string;
  mapImage: string;
  name: string;
  address: string;
  callLines: string[];
  email: string;
  hours: string;
  mapHref: string;
}

interface OfficeAddressProps {
  headOffice?: HeadOffice;
  branchOffices?: BranchOffice[];
}

const OfficeAddress: React.FC<OfficeAddressProps> = ({ headOffice = HEAD_OFFICE, branchOffices = BRANCH_OFFICES }) => {
  const isMobile = useIsMobile(900);

  return (
    <section className={styles["address-section"]}>
      <div className={styles["head-office"]}>
        {isMobile && (
          <div className={styles["head-office-image"]}>
            <CustomImage src={headOffice.image} alt={headOffice.name} layout="fill" objectFit="cover" />{" "}
          </div>
        )}

        <div className={styles["head-office-info"]}>
          <div className={styles["badge-container"]}>
            <p>{headOffice.badge}</p>

            <h2 className={styles["head-office-name"]}>{headOffice.name}</h2>
          </div>

          <div className={styles["head-field"]}>
            <span className={styles["head-label"]}>
              <MapPin />
              Address
            </span>
            <p className={styles["head-value"]}>{headOffice.address}</p>
          </div>

          <div className={styles["head-field-row"]}>
            <div className={styles["head-field"]}>
              <span className={styles["head-label"]}>
                <Phone />
                Call
              </span>
              {headOffice.callLines.map((line) => (
                <p key={line} className={styles["head-value"]}>
                  {line}
                </p>
              ))}
            </div>

            <div className={styles["head-field"]}>
              <span className={styles["head-label"]}>
                <Mail />
                Email
              </span>
              <p className={styles["head-email-link"]}>{headOffice.email}</p>
            </div>
          </div>

          <div className={styles["head-field"]}>
            <span className={styles["head-label"]}>
              <Clock />
              Office Hours
            </span>
            <p className={styles["head-value"]}>{headOffice.hours}</p>
          </div>

          <a href={headOffice.mapHref} target="_blank" rel="noopener noreferrer" className={styles["map-button"]}>
            <button className={styles["show-on-gmap-btn"]}>
              Show on Google Maps <Chevron width={12} height={12} color="#ff7361" />
            </button>
          </a>
        </div>

        {!isMobile && (
          <div className={styles["head-office-image"]}>
            <CustomImage src={headOffice.image} alt={headOffice.name} layout="fill" objectFit="cover" />{" "}
          </div>
        )}
      </div>

      <div className={styles["branch-offices-container"]}>
        <h2 className={styles["section-title"]}>Student Counselling Offices</h2>
        <div className={styles["branch-grid"]}>
          {branchOffices.map((office) => (
            <div key={office.id} className={styles["branch-card"]}>
              <div className={styles["branch-map"]}>
                <LazyMedia width="100%" height="190px" src={office?.mapImage} />
              </div>

              <div className={styles["branch-content"]}>
                <h3 className={styles["office-name"]}>{office.name}</h3>
                <div className={styles["branch-field"]}>
                  <span className={styles["branch-field-label"]}>
                    <MapPin />
                    Address
                  </span>
                  <p className={styles["branch-value"]}>{office.address}</p>
                </div>

                <div className={styles["branch-content-body"]}>
                  <div>
                    <div className={styles["branch-field-row"]}>
                      <div className={styles["branch-field"]}>
                        <span className={styles["branch-field-label"]}>
                          <Phone />
                          Call
                        </span>
                        {office.callLines.map((line) => (
                          <p key={line} className={styles["branch-value"]}>
                            {line}
                          </p>
                        ))}
                      </div>

                      <div className={styles["branch-field"]}>
                        <span className={styles["branch-field-label"]}>
                          <Mail />
                          Email
                        </span>
                        <p className={styles["branch-email-link"]}>{office.email}</p>
                      </div>
                    </div>

                    <div className={styles["hours-container"]}>
                      <span className={styles["branch-field-label"]}>
                        <Clock />
                        Office hours
                      </span>
                      <p className={styles["branch-value"]}>{office.hours}</p>
                    </div>
                  </div>

                  <div>
                    <a href={office.mapHref} target="_blank" rel="noopener noreferrer" className={styles["map-link"]}>
                      Show on Google Maps
                      <Chevron width={12} height={12} color="#ff7361" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OfficeAddress;
